import React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import ClassDataContents from "../../../templates/teacher/ClassDataContents";
import {
  fetchTeachingMaterials,
  fetchListLessons,
  fetchPortfolio,
  fetchStudentActionList,
  fetchStudents,
  fetchClasses,
  resetStudentPassword,
  updateComment,
  deleteComment,
  toggleComment,
  classDataPath,
  RESET_STUDENT_PASSWORD_SUCCESS_MESSAGE,
} from "../../../teacherStore";
import { Context } from "../../../teacherStore";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import { parseQueryParams } from "../../../libs/Util";
import { IStudent } from "../../../state";
import { TEACHING_MATERIAL_KEYWORDS } from "../../../constants/teaching_materials";

export type IClassDataScreenType = "response" | "portfolio" | "survey";

export default () => {
  const { state, dispatch } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(state);
  const [materialCalling, materialPeep] = useWaitApiCall(state);
  const history = useHistory();
  const { pathname } = useLocation();
  const params = useParams<{
    class_id: string;
    student_id: string;
  }>();
  const student_id = parseInt(params.student_id);
  const class_id = parseInt(params.class_id);

  const currentStudent = React.useMemo(() => {
    return state.students.find(s => s.id === student_id);
  }, [state.students, student_id]);

  const nameClass = React.useMemo(() => {
    return state.classes.find(c => c.id === class_id)?.full_name ?? "";
  }, [state.classes, class_id]);

  const screenType = pathname.split("/").pop() as IClassDataScreenType;
  const teachingMaterialId = Number(parseQueryParams()["teaching_material_id"]);

  React.useEffect(() => {
    if (state.students.length === 0) {
      dispatch(fetchStudents([class_id]));
    }
  }, [dispatch, state.students, class_id]);

  React.useEffect(() => {
    if (state.success_message === RESET_STUDENT_PASSWORD_SUCCESS_MESSAGE) {
      dispatch(peep(fetchStudents([class_id])));
    }
  }, [dispatch, peep, state.success_message, class_id]);

  React.useEffect(() => {
    if (state.classes.length === 0) {
      dispatch(fetchClasses());
    }
  }, [dispatch, state.classes]);

  React.useEffect(() => {
    dispatch(materialPeep(fetchTeachingMaterials([class_id])));
  }, [dispatch, materialPeep, class_id]);

  const currentMaterial = React.useMemo(
    () => state.teaching_materials.find(m => m.id === teachingMaterialId),
    [state.teaching_materials, teachingMaterialId],
  );

  const isBaseMaterial =
    currentMaterial?.kind?.includes(TEACHING_MATERIAL_KEYWORDS.BASE) ?? false;

  React.useEffect(() => {
    if (!teachingMaterialId || !student_id || !class_id || !currentMaterial)
      return;
    dispatch(peep(fetchListLessons(student_id, class_id, teachingMaterialId)));
    if (!isBaseMaterial) {
      dispatch(peep(fetchPortfolio(student_id, class_id, teachingMaterialId)));
    }
    dispatch(peep(fetchStudentActionList(student_id, teachingMaterialId)));
  }, [
    dispatch,
    peep,
    student_id,
    class_id,
    teachingMaterialId,
    currentMaterial,
    isBaseMaterial,
  ]);

  const handleTeachingMaterialChange = React.useCallback(
    (material_id: number) => {
      history.replace(
        `${classDataPath}/${class_id}/student/${student_id}/${screenType}?teaching_material_id=${material_id}`,
      );
    },
    [history, class_id, student_id, screenType],
  );

  const handleChangeStudent = React.useCallback(
    (student: IStudent) => {
      if (!student) return;
      history.replace(
        `${classDataPath}/${class_id}/student/${student.id}/${screenType}?teaching_material_id=${teachingMaterialId}`,
      );
    },
    [history, class_id, screenType, teachingMaterialId],
  );

  const onBackFunc = React.useCallback(
    () => history.push(`${classDataPath}/${class_id}`, { nameClass }),
    [history, class_id, nameClass],
  );

  const handleResetStudentPassword = React.useCallback(() => {
    if (calling || !currentStudent) return;
    dispatch(peep(resetStudentPassword(currentStudent.id)));
  }, [dispatch, peep, calling, currentStudent]);

  const handleComment = React.useCallback(
    (content: string, answerId: number, comment_id?: number) => {
      if (!comment_id) return;
      dispatch(updateComment(answerId, content, comment_id));
    },
    [dispatch],
  );

  const handleDeleteComment = React.useCallback(
    (answer_id: number, comment_id: number) => {
      dispatch(deleteComment(answer_id, comment_id));
    },
    [dispatch],
  );

  const handleToggleComment = React.useCallback(
    (answer_id: number, comment_id: number, is_hidden: boolean) => {
      dispatch(toggleComment(answer_id, comment_id, is_hidden));
    },
    [dispatch],
  );

  return (
    <ClassDataContents
      calling={calling}
      materialCalling={materialCalling}
      student={currentStudent}
      students={state.students}
      teaching_materials={state.teaching_materials}
      classId={class_id}
      lessonsByTeachingMaterial={state.lessonsByTeachingMaterial}
      portfolio={state.portfolio}
      teachingMaterialId={teachingMaterialId}
      studentActionList={state.studentActionList}
      currentTeacherId={state.teacher.id}
      onBack={onBackFunc}
      onTeachingMaterialChange={handleTeachingMaterialChange}
      onChangeStudent={handleChangeStudent}
      onReissueStudentPassword={handleResetStudentPassword}
      handleComment={handleComment}
      handleDeleteComment={handleDeleteComment}
      handleToggleComment={handleToggleComment}
    />
  );
};
